import { useState, useEffect } from 'react'
import { UserAPI } from '@certi/shared/api/user/UserAPI'
import { useAuth } from '@/hooks/useAuth'
import type { BiddingEligibilityResponse } from '@certi/shared/types/types.user'
import VerificationModal from './VerificationModal' // Import the modal
import { MdEmail, MdPhone, MdLocationOn, MdCreditCard, MdWarning } from 'react-icons/md' // Import Material Design Icons

export default function VerificationProgress() {
  const { user, accessToken, session } = useAuth()
  const [eligibility, setEligibility] = useState<BiddingEligibilityResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false) // State for modal
  const token = accessToken ?? session?.accessToken

  useEffect(() => {
    const fetchEligibility = async () => {
      if (!user?.id || !token) {
        setLoading(false)
        return
      }

      try {
        const data = await UserAPI.getBiddingEligibility(user.id, token)
        setEligibility(data)
      } catch (error) {
        console.error('Failed to fetch bidding eligibility:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchEligibility()
  }, [user?.id, token])

  if (loading) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-3"></div>
        <div className="h-2 bg-gray-200 rounded w-full mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-2/3"></div>
      </div>
    )
  }

  if (!eligibility || eligibility.eligible) {
    return null
  }

  // Helper function to map requirement strings to user-friendly text and icons
  const getRequirementAction = (requirement: string) => {
    const lower = requirement.toLowerCase()
    if (lower.includes('email')) return { text: 'Verify your email', icon: <MdEmail /> }
    if (lower.includes('phone')) return { text: 'Add your phone number', icon: <MdPhone /> }
    if (lower.includes('address')) return { text: 'Add a shipping address', icon: <MdLocationOn /> }
    if (lower.includes('payment')) return { text: 'Add a payment method', icon: <MdCreditCard /> }
    return { text: requirement, icon: <MdWarning /> }
  }

  // Reintroduce progress calculations
  const totalRequirements = 4 // Email, Phone, Address, Payment - assuming fixed for now
  const completedRequirements = totalRequirements - eligibility.missingRequirements.length
  const progressPercentage = (completedRequirements / totalRequirements) * 100

  // Determine the very next action to prompt the user with
  const nextRequirement = eligibility.missingRequirements[0]
  const nextAction = getRequirementAction(nextRequirement)

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm text-center">
        {/* Progress Bar and Text */}
        <div className="mb-4">
          <div className="flex justify-between items-center text-sm text-gray-600 mb-1">
            <span>Progress:</span>
            <span>{completedRequirements}/{totalRequirements} Complete</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-[#0B4D2A] h-2.5 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
        </div>

        <div className="flex justify-center items-center mb-3">
            <span className="text-2xl mr-3">{nextAction.icon}</span>
            <h3 className="text-lg font-semibold text-gray-900">
            {nextAction.text}
            </h3>
        </div>

        <p className="text-sm text-gray-500 mb-4">
          You will soon be able to bid. Complete your profile to get started.
        </p>

        <button
          onClick={() => setIsModalOpen(true)}
          className="bg-[#0B4D2A] text-white font-semibold py-2 px-6 rounded-lg hover:bg-opacity-90 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0B4D2A]"
        >
          Let's Go
        </button>
      </div>

      <VerificationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        eligibility={eligibility}
      />
    </>
  )
}
